/* ==========================================================================
   PB CUSTOM - "Go Green" compostable packaging cart add-on
   Paired with snippets/pb-compostable-addon.liquid + assets/pb-compostable-addon.css

   HOW IT WORKS
   ------------
   The card's visual state is rendered by Liquid, not by this script. Adding or
   removing re-renders the cart drawer / main cart sections through the theme's
   own pub/sub pipeline, so the card, the line items and the totals all update
   together from a single source of truth (the real cart).

   All cart writes go through /cart/update.js with an absolute `updates`
   quantity (1 to add, 0 to remove) - the exact same mechanism the Hyper theme
   already uses for its gift-wrapping add-on. Because the quantity is absolute
   rather than incremental, it is impossible to create a duplicate line or a
   quantity above 1.

   This script only ever touches the one compostable-packaging variant. It never
   writes cart attributes, notes, discounts or line item properties, so Easify
   Options and everything else in the cart are left alone.

   It also keeps the customer-facing item count in sync - see syncCartCounts().

   Safe to delete along with the other two files.
   ========================================================================== */

(function () {
  'use strict';

  var PRODUCT_HANDLE = 'compostable-packaging';
  var CARD_SELECTOR = '[data-pb-compostable]';
  var ADD_SELECTOR = '[data-pb-compostable-add]';
  var REMOVE_SELECTOR = '[data-pb-compostable-remove]';

  var inFlight = false;

  function cartUpdateUrl() {
    var root = (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_update_url) || null;
    return root || '/cart/update.js';
  }

  /* Ask the theme which sections need re-rendering (cart drawer, main cart,
     and anything else that opts in). Same event the theme's own cart code uses. */
  function sectionsToUpdate() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function publishCart(cart) {
    if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    } else {
      /* Extremely defensive: if the theme's pub/sub ever goes away, fall back to
         a reload so the cart total on screen can never be wrong. */
      window.location.reload();
    }
  }

  /**
   * Set the compostable packaging line to an absolute quantity.
   * @param {string|number} variantId
   * @param {number} quantity 1 to add, 0 to remove.
   * @param {HTMLElement|null} card the card that triggered this, for the busy state.
   */
  function setQuantity(variantId, quantity, card) {
    if (inFlight || !variantId) return;
    inFlight = true;

    /* Show the busy state on every copy of the card (page + drawer can both exist). */
    var cards = document.querySelectorAll(CARD_SELECTOR);
    Array.prototype.forEach.call(cards, function (el) {
      el.classList.add('is-busy');
    });

    var updates = {};
    updates[variantId] = quantity;

    fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        updates: updates,
        sections: sectionsToUpdate(),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        inFlight = false;
        publishCart(cart);
      })
      .catch(function (error) {
        inFlight = false;
        console.error('[pb-compostable-addon]', error);
        Array.prototype.forEach.call(document.querySelectorAll(CARD_SELECTOR), function (el) {
          el.classList.remove('is-busy');
        });
      });
  }

  /* --- Customer-facing item count -------------------------------------------
     The packaging upgrade is a real line item, so cart.item_count includes it and
     a cart holding 1 product + the upgrade would read "2". The Liquid side of this
     is snippets/pb-cart-display-count.liquid, which renders the adjusted number
     into all four <cart-count> elements on first paint. This is its live
     counterpart: the theme's own CartCount element re-writes itself straight from
     cart.item_count on every AJAX update, so we re-apply the subtraction after it.

     Display only - the cart, the subtotal and the order are untouched, and the
     theme's own `cart.item_count > 0` empty-state checks are deliberately left
     using the real count. */

  function displayCount(cart) {
    var packaging = 0;
    (cart.items || []).forEach(function (item) {
      if (item.handle === PRODUCT_HANDLE) packaging += item.quantity;
    });
    return Math.max(0, (cart.item_count || 0) - packaging);
  }

  function syncCartCounts(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;
    var count = displayCount(cart);

    Array.prototype.forEach.call(document.querySelectorAll('cart-count'), function (el) {
      var text = count > 99 ? '99+' : String(count);
      if (el.dataset.type === 'blank') text = '(' + text + ')';

      /* Mirrors the theme's own CartCount.onCartUpdate formatting. */
      if (el.classList.contains('cart-count--absolute') && count > 99) {
        el.innerHTML = '<span class="text-sm-extra">' + text + '</span>';
        el.classList.add('cart-count--small-medium');
      } else {
        el.innerText = text;
        el.classList.remove('cart-count--small-medium');
      }

      el.hidden = count === 0;
    });
  }

  /* The theme's CartCount updates itself synchronously while the cartUpdate event
     is being published. Deferring by a task guarantees we run after it, whichever
     order the two subscribers happen to be registered in. */
  function scheduleCountSync(cart) {
    setTimeout(function () {
      syncCartCounts(cart);
    }, 0);
  }

  if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cart && !event.cart.errors) scheduleCountSync(event.cart);
    });
  }

  /* --- Click handling -------------------------------------------------------
     Delegated from document because the cards are replaced wholesale (innerHTML)
     every time the cart re-renders - a directly bound listener would be lost. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var addBtn = target.closest(ADD_SELECTOR);
    var removeBtn = target.closest(REMOVE_SELECTOR);
    if (!addBtn && !removeBtn) return;

    var card = (addBtn || removeBtn).closest(CARD_SELECTOR);
    if (!card) return;

    event.preventDefault();
    /* The main cart page wraps its footer in <form id="cart">; stop the click
       from bubbling into any submit handling. */
    event.stopPropagation();

    setQuantity(card.getAttribute('data-pb-variant-id'), addBtn ? 1 : 0, card);
  });

  /* --- Safety guards --------------------------------------------------------
     Run after every cart render. Two things they enforce:
       1. Quantity can never exceed 1 (e.g. if someone bumps the line item's own
          quantity stepper in the cart list).
       2. The upgrade can't be left behind on its own once every real product has
          been removed. */
  function runGuards() {
    if (inFlight) return;

    var card = document.querySelector(CARD_SELECTOR);
    if (!card) return;

    var variantId = card.getAttribute('data-pb-variant-id');
    var quantity = parseInt(card.getAttribute('data-pb-quantity'), 10) || 0;
    var isOrphan = card.getAttribute('data-pb-orphan') === 'true';

    if (isOrphan) {
      setQuantity(variantId, 0, card);
      return;
    }

    if (quantity > 1) {
      setQuantity(variantId, 1, card);
    }
  }

  document.addEventListener('cart:updated', function (event) {
    /* Deferred so the theme has finished swapping the section markup in. */
    setTimeout(runGuards, 0);
    if (event && event.detail && event.detail.cart) scheduleCountSync(event.detail.cart);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runGuards);
  } else {
    runGuards();
  }
})();
